import React, { useCallback, useEffect, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import Tooltip from '@material-ui/core/Tooltip'
import FormatColorTextIcon from '@material-ui/icons/FormatColorText'
import FormatClearIcon from '@material-ui/icons/FormatClear'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import {
  EditorContent,
  EditorState,
  getCore,
  getEditorExtensions,
  handlePlainTextPaste,
  selectTextLineOnTripleClick,
  serializePlainTextClipboard,
  startHistoryGroup,
  TiptapBubbleMenu,
  undoHistory,
  useEditor
} from '@golden/tiptap-react'
import { isEqual } from '@golden/utils'
import useT from '../../../../i18ns/admin/useT'

const HIGHLIGHT_COLOR = '#f44336'

const isUndoShortcut = (event: KeyboardEvent) => {
  const { code, isComposing } = event

  // 已由其他 handler 處理時，不要重複執行復原。
  if (event.defaultPrevented) return false

  // 輸入法還在組字時執行復原，可能會中斷尚未完成的文字輸入。
  if (isComposing) return false

  // Shift + Ctrl/Cmd + Z 是重做快捷鍵，不屬於這裡處理的復原操作。
  if (event.shiftKey) return false

  // Windows 使用 Ctrl，macOS 使用 Cmd。
  const hasUndoModifier = event.ctrlKey || event.metaKey
  // key 判斷一般鍵盤輸入，code 則相容不同鍵盤配置下的實體 Z 鍵。
  const isZKey = event.key.toLowerCase() === 'z' || code === 'KeyZ'

  return hasUndoModifier && isZKey
}

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%'
  },
  bubbleMenu: {
    display: 'none',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    '[data-tippy-root] &': {
      display: 'flex'
    }
  },
  bubbleMenuHidden: {
    display: 'none !important'
  },
  editorContent: {
    boxSizing: 'border-box',
    padding: '18.5px 14px',
    '& .ProseMirror': {
      lineHeight: '19px',
      '& p': {
        margin: 0
      },
      '&:focus': {
        outline: 'none'
      }
    }
  },
  placeholder: {
    '&::before': {
      content: 'attr(data-placeholder)',
      float: 'left',
      color: '#ced4da',
      pointerEvents: 'none',
      height: 0
    }
  }
}))

export interface RedTextBubbleEditorProps {
  value: EditorState
  disabled: boolean
  onChange: (value: EditorState) => void
  placeholder?: string
  editorContentClassName?: string
}

export const RedTextBubbleEditor: React.FC<RedTextBubbleEditorProps> = (props) => {
  const {
    value,
    disabled,
    onChange,
    placeholder = '',
    editorContentClassName
  } = props
  const classes = useStyles()
  const { t } = useT()
  const [editorContainer, setEditorContainer] = useState<HTMLDivElement | null>(null)
  const previousDisabled = useRef(disabled)
  const disabledRef = useRef(disabled)
  const minimumUndoDepth = useRef(0)
  disabledRef.current = disabled
  const editor = useEditor({
    content: value.content,
    editable: !disabled,
    editorProps: {
      attributes: {
        tabindex: '0'
      },
      clipboardTextSerializer: serializePlainTextClipboard,
      handleKeyDown: (view, event) => {
        if (disabledRef.current || !isUndoShortcut(event)) return false

        // 部分 Windows 環境無法透過 Tiptap v2 beta 的 Ctrl+Z 復原，
        // 因此在內建快捷鍵前攔截，且只允許復原本次編輯產生的內容。
        event.preventDefault()
        undoHistory(view, minimumUndoDepth.current)
        return true
      },
      handlePaste: handlePlainTextPaste,
      handleTripleClick: selectTextLineOnTripleClick
    },
    extensions: async () => {
      const core = await getCore()
      return await getEditorExtensions(core, {
        placeholder: () => placeholder,
        emptyEditorClass: classes.placeholder
      })
    },
    onUpdate: ({ editor }) => {
      onChange({
        position: {
          from: editor.state.selection.from,
          to: editor.state.selection.to
        },
        content: editor.getJSON()
      })
    }
  })

  useEffect(() => {
    if (!editor || isEqual(editor.getJSON(), value.content)) return
    editor.commands.setContent(value.content)
  }, [editor, value.content])

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled)
    if (!editor.state.selection.empty) {
      editor.commands.setTextSelection(editor.state.selection.to)
    }
    if (previousDisabled.current && !disabled) {
      minimumUndoDepth.current = startHistoryGroup(editor.view)
      editor.commands.focus()
    }
    previousDisabled.current = disabled
  }, [disabled, editor])

  // 點擊編輯器時，Ctrl + A 可全選編輯器裡的內容
  const handleReadOnlySelectAll = useCallback<React.KeyboardEventHandler<HTMLDivElement>>((event) => {
    // Editor 尚未建立或目前可編輯時，交由 Tiptap 原本的鍵盤行為處理。
    if (!editor || !disabled) return

    // 唯讀狀態只攔截 Windows 的 Ctrl + A 與 macOS 的 Cmd + A。
    const isSelectAllShortcut = event.key.toLowerCase() === 'a' && (event.ctrlKey || event.metaKey)
    if (!isSelectAllShortcut) return

    event.preventDefault()
    editor.commands.selectAll()
  }, [disabled, editor])

  if (!editor) return null

  const textColorLabel = t('common.textColor')
  const clearFormatLabel = t('common.clearFormat')

  // 共用 BubbleMenu 會在每次選取更新時覆寫 tippyOptions.getReferenceClientRect，
  // 導致外部指定的選取終點定位無法生效，長內容跨過捲動區域時選單可能超出編輯器。
  // 若修改其共用定位邏輯會影響所有使用 Tiptap 的專案，因此此處改用官方元件，將調整限制在本編輯器。
  return (
    <div ref={setEditorContainer} className={classes.root}>
      {editorContainer && (
        <TiptapBubbleMenu
          editor={editor}
          className={clsx(classes.bubbleMenu, disabled && classes.bubbleMenuHidden)}
          tippyOptions={{
            getReferenceClientRect: () => {
              const { left, right, top, bottom } = editor.view.coordsAtPos(editor.state.selection.head)
              return new DOMRect(left, top, right - left, bottom - top)
            },
            popperOptions: {
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    boundary: editorContainer
                  }
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: editorContainer,
                    altAxis: true,
                    tether: false
                  }
                }
              ]
            }
          }}
        >
          <Tooltip
            title={textColorLabel}
            arrow
            disableFocusListener
            PopperProps={{ disablePortal: true }}
          >
            <span>
              <Button
                aria-label={textColorLabel}
                disabled={disabled}
                onClick={() => editor.chain().focus().setTextColor(HIGHLIGHT_COLOR).run()}
              >
                <FormatColorTextIcon style={{ color: disabled ? undefined : HIGHLIGHT_COLOR }}/>
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={clearFormatLabel}
            arrow
            disableFocusListener
            PopperProps={{ disablePortal: true }}
          >
            <span>
              <Button
                aria-label={clearFormatLabel}
                disabled={disabled}
                onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
              >
                <FormatClearIcon/>
              </Button>
            </span>
          </Tooltip>
        </TiptapBubbleMenu>
      )}
      <EditorContent
        editor={editor}
        className={clsx(classes.editorContent, editorContentClassName)}
        onKeyDown={handleReadOnlySelectAll}
      />
    </div>
  )
}

export default React.memo(RedTextBubbleEditor)
